import {
  ChatEditIcon,
  ChatReplyIcon,
  ChatTranslateIcon,
  ChatTrashIcon,
  CopyIcon,
} from '../../../assets/svg';
import { ConversationMessageWithStatus } from '../../../types/conversation.types';
import { copyMessageBody } from '../helpers/chatClipboard';
import { MessageTranslationState } from '../hooks/useMessageTranslations';
import { HoverActions, MessageActionButton } from './MessageBubble.styled';

/**
 * Reply / copy / edit / delete, revealed on hover in the gutter beside the
 * bubble.
 *
 * `canModify` is stricter than "your side": in a support thread a colleague's
 * reply sits on your side because the vGrubs side is one voice to the merchant,
 * but the API only accepts edits to messages you actually wrote - so showing
 * these on theirs would offer something that cannot work. Copy has no such
 * rule - reading a message is already having its text, and neither has
 * Translate, which only changes what the reader sees.
 */
export const MessageActions = ({
  message,
  own,
  canModify,
  onReply,
  onEdit,
  onDelete,
  translation,
  onTranslate,
}: {
  message: ConversationMessageWithStatus;
  own: boolean;
  canModify: boolean;
  translation?: MessageTranslationState | null;
  /** Absent where the message offers no translation - see `canTranslate`. */
  onTranslate?: (message: ConversationMessageWithStatus) => void;
  onReply?: (message: ConversationMessageWithStatus) => void;
  onEdit?: (message: ConversationMessageWithStatus) => void;
  onDelete?: (message: ConversationMessageWithStatus) => void;
}) => (
  <HoverActions $own={own}>
    {onReply && (
      <MessageActionButton
        type="button"
        aria-label="Reply"
        onClick={() => onReply(message)}
      >
        <ChatReplyIcon />
      </MessageActionButton>
    )}

    {/* A deleted message arrives with a null body, so this also keeps the
        button off the "Message deleted" stub. */}
    {message.body && (
      <MessageActionButton
        type="button"
        aria-label="Copy message"
        onClick={() => copyMessageBody(message.body!)}
      >
        <CopyIcon />
      </MessageActionButton>
    )}

    {onTranslate && (
      <MessageActionButton
        type="button"
        aria-label={
          translation?.status === 'shown' ? 'See original' : 'Translate'
        }
        disabled={translation?.status === 'loading'}
        onClick={() => onTranslate(message)}
      >
        <ChatTranslateIcon />
      </MessageActionButton>
    )}

    {/* Nothing to edit on a message that is only an attachment. */}
    {canModify && onEdit && message.body && (
      <MessageActionButton
        type="button"
        aria-label="Edit message"
        onClick={() => onEdit(message)}
      >
        <ChatEditIcon />
      </MessageActionButton>
    )}

    {canModify && onDelete && (
      <MessageActionButton
        type="button"
        $danger
        aria-label="Delete message"
        onClick={() => onDelete(message)}
      >
        <ChatTrashIcon />
      </MessageActionButton>
    )}
  </HoverActions>
);
